import React, { useCallback, useEffect, useState } from "react";
import { Search, RefreshCw } from "lucide-react";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { maintAnalyticsApi as api } from "../../../api/maintenance/analytics";
import type { ActiveSession, ConnectorStatus, RecentSession } from "../../../api/maintenance/analytics";
import SessionDetailDrawer, { SessionBasic, statusPill } from "./SessionDetailDrawer";
import "../shared/maintenance-analytics.scss";
import { useNotice } from "../shared/useNotice";

const num = (v: number | null | undefined, d = 0) =>
  v == null ? "—" : v.toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });
const money = (v: number | null | undefined) => (v == null ? "—" : `₹${num(v, 2)}`);
const kw = (v: number | null | undefined) => (v == null ? "—" : `${num(v, 2)} kW`);
const ts = (s: string | null) => (s ? new Date(s).toLocaleString("en-IN", { hour12: false }) : "—");

type Tab = "live" | "connectors" | "recent";

const SessionsConsole: React.FC = () => {
  const [tab, setTab] = useState<Tab>("live");
  const [error, setError] = useNotice("error", "");
  const [loading, setLoading] = useState(false);
  const [auto, setAuto] = useState(true);

  const [live, setLive] = useState<ActiveSession[]>([]);
  const [connectors, setConnectors] = useState<ConnectorStatus[]>([]);
  const [recent, setRecent] = useState<RecentSession[]>([]);
  const [recentLimit, setRecentLimit] = useState(100);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [problemsOnly, setProblemsOnly] = useState(false);

  const [detailId, setDetailId] = useState<number | null>(null);
  const [basic, setBasic] = useState<SessionBasic | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (tab === "live") setLive((await api.activeSessions()).data);
      else if (tab === "connectors") setConnectors((await api.connectorStatus({ search: search || undefined, problems_only: problemsOnly })).data);
      else setRecent((await api.recentSessions({ limit: recentLimit })).data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [tab, search, problemsOnly, recentLimit]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!auto || tab === "recent") return;
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [auto, tab, load]);

  const openLive = (s: ActiveSession) => {
    setBasic({
      session_id: s.session_id, station: s.station_name, connector: s.connector_name,
      device: s.device_name || s.device, user_name: s.user_name, phone: s.phone,
      organization: s.organization, consumed_kwh: s.consumed_kwh, charge_rate_kw: s.charge_rate_kw, status: s.status,
    });
    setDetailId(s.session_id);
  };
  const openRecent = (s: RecentSession) => {
    setBasic({
      session_id: s.session_id, station: s.station, connector: s.connector_name, device: s.device,
      user_name: s.user_name, phone: s.phone, organization: s.organization, consumed_kwh: s.consumed_kwh,
      charge_rate_kw: s.charge_rate_kw, total_cost: s.total_cost, duration_mins: s.duration_mins, status: s.status,
    });
    setDetailId(s.session_id);
  };

  const TABS: { id: Tab; label: string }[] = [
    { id: "live", label: `Live sessions${live.length ? ` (${live.length})` : ""}` },
    { id: "connectors", label: "Connector status" },
    { id: "recent", label: "Recent sessions" },
  ];

  return (
    <MaintenanceLayout title="Sessions & Live Ops (Maintenance)">
      <div className="ma">
        <div className="ma-tabs">
          {TABS.map((t) => <button key={t.id} className={`ma-tab ${tab === t.id ? "on" : ""}`} onClick={() => setTab(t.id)}>{t.label}</button>)}
        </div>

        <div className="ma-toolbar">
          {tab === "connectors" && (
            <>
              <form className="ma-search" onSubmit={(e) => { e.preventDefault(); setSearch(searchInput); }}>
                <Search size={15} /><input placeholder="Station / device / org…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
              </form>
              <label className="ma-btn" style={{ cursor: "pointer" }}>
                <input type="checkbox" checked={problemsOnly} onChange={(e) => setProblemsOnly(e.target.checked)} /> Problems only
              </label>
            </>
          )}
          {tab === "recent" && (
            <label className="ma-select">Rows
              <select value={recentLimit} onChange={(e) => setRecentLimit(Number(e.target.value))}>
                {[50, 100, 200, 500].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          )}
          <span className="ma-spacer" />
          {tab !== "recent" && (
            <label className="ma-btn" style={{ cursor: "pointer" }}>
              <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> Auto-refresh 30s
            </label>
          )}
          <button className="ma-btn" onClick={load}><RefreshCw size={14} /> Refresh</button>
        </div>

        {error && <div className="ma-error">{error}</div>}
        {(tab === "live" || tab === "recent") && <p className="ma-hint" style={{ marginBottom: 10 }}>Click any row for full session detail.</p>}

        <div className="ma-card">
          {loading && <p className="ma-loading">Loading…</p>}

          {!loading && tab === "live" && (
            <div className="ma-tablewrap">
              <table className="ma-table">
                <thead><tr><th className="num">#</th><th>Station</th><th>Device / connector</th><th>User</th><th>Started</th><th className="num">Active</th><th className="num">kWh</th><th className="num">Rate</th><th>Status</th></tr></thead>
                <tbody>
                  {live.map((s) => (
                    <tr key={s.session_id} className="clickable" onClick={() => openLive(s)}>
                      <td className="num">{s.session_id}</td>
                      <td>{s.station_name || "—"}<div className="sub">{s.organization || ""}</div></td>
                      <td>{s.device_name || s.device || "—"}<div className="sub">{s.connector_name || ""}</div></td>
                      <td>{s.user_name || "—"}<div className="sub">{s.phone || ""}</div></td>
                      <td className="sub">{ts(s.session_started_on)}</td>
                      <td className="num">{s.mins_active != null ? `${s.mins_active}m` : "—"}</td>
                      <td className="num">{num(s.consumed_kwh, 2)}</td>
                      <td className="num">{kw(s.charge_rate_kw)}</td>
                      <td><span className={`ma-pill ${statusPill(s.status)}`}>{s.status || "—"}</span></td>
                    </tr>
                  ))}
                  {!live.length && <tr><td colSpan={9} className="ma-empty">No active sessions</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {!loading && tab === "connectors" && (
            <div className="ma-tablewrap">
              <table className="ma-table">
                <thead><tr><th>Device</th><th>Connector</th><th>Station</th><th>Status</th><th>Last error</th><th className="num">30d</th><th>Seen</th></tr></thead>
                <tbody>
                  {connectors.map((c) => (
                    <tr key={c.connector_id}>
                      <td>{c.device || c.unique_name || "—"}<div className="sub">{c.organization || ""}</div></td>
                      <td>{c.connector_name || "—"}<div className="sub">{c.socket_name || ""}{c.capacity_kwh ? ` · ${c.capacity_kwh}kW` : ""}</div></td>
                      <td>{c.station || "—"}</td>
                      <td><span className={`ma-dot ${c.online ? "on" : "off"}`} /><span className={`ma-pill ${statusPill(c.effective_status)}`}>{c.effective_status || c.current_status || "—"}</span></td>
                      <td>{c.error_code ? <span className="ma-pill bad" title={c.error_info || ""}>{c.error_code}</span> : <span className="sub">—</span>}</td>
                      <td className="num">{num(c.sessions_30d)}</td>
                      <td className="sub">{ts(c.last_active)}</td>
                    </tr>
                  ))}
                  {!connectors.length && <tr><td colSpan={7} className="ma-empty">No connectors</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {!loading && tab === "recent" && (
            <div className="ma-tablewrap">
              <table className="ma-table">
                <thead><tr><th className="num">#</th><th>Started</th><th>Station</th><th>Device / conn</th><th>User</th><th className="num">Dur</th><th className="num">kWh</th><th className="num">Rate</th><th className="num">Cost</th><th>Stop reason</th></tr></thead>
                <tbody>
                  {recent.map((s) => (
                    <tr key={s.session_id} className="clickable" onClick={() => openRecent(s)}>
                      <td className="num">{s.session_id}</td>
                      <td className="sub">{ts(s.session_started_on)}</td>
                      <td>{s.station || "—"}<div className="sub">{s.organization || ""}</div></td>
                      <td>{s.device || "—"}<div className="sub">{s.connector_name || ""}</div></td>
                      <td>{s.user_name || "—"}<div className="sub">{s.phone || ""}</div></td>
                      <td className="num">{s.duration_mins != null ? `${s.duration_mins}m` : "—"}</td>
                      <td className="num">{num(s.consumed_kwh, 2)}</td>
                      <td className="num">{kw(s.charge_rate_kw)}</td>
                      <td className="num">{money(s.total_cost)}</td>
                      <td className="sub">{s.stop_reason || "—"}</td>
                    </tr>
                  ))}
                  {!recent.length && <tr><td colSpan={10} className="ma-empty">No sessions</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <SessionDetailDrawer sessionId={detailId} fallback={basic} onClose={() => setDetailId(null)} />
    </MaintenanceLayout>
  );
};

export default SessionsConsole;
